/**
 * SortableGroupCard Component
 * 
 * Displays an account group card with drag-and-drop functionality.
 */

import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AccountGroup } from "../hooks/useAccountSettingsUIState";
import { Account } from "../../../types";

export interface SortableGroupCardProps {
  group: AccountGroup;
  inUse: boolean;
  accountsUsingGroup: Account[];
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, group: AccountGroup) => void;
}

/**
 * Sortable group card component
 */
export default function SortableGroupCard({
  group,
  inUse,
  accountsUsingGroup,
  onMenuOpen,
}: SortableGroupCardProps): React.ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 0}
      sx={{
        p: 0.75,
        border: 1,
        borderColor: "divider",
        borderRadius: 0.5,
        "&:hover": { boxShadow: 2 },
        transition: "box-shadow 0.2s",
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        {/* Drag Handle - touchAction: none so touch starts drag, not scroll */}
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            color: "text.secondary",
            p: 0.5,
            touchAction: "none",
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>

        {/* Group Name and Description */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {group.name}
          </Typography>
          {group.description && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: "block" }}>
              {group.description}
            </Typography>
          )}
        </Box>

        {/* Menu Button */}
        <IconButton
          size="small"
          onClick={(e) => onMenuOpen(e, group)}
          sx={{ flexShrink: 0, p: 0.25, ml: 0.5 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}
